<script setup lang="ts">
import { computed, Ref, ref, watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { SerializedChartType } from '@/composables/charts/influx-charts/chart-builders/ChartInterfaces'
import GenericCustomDialog from '@/components/GenericCustomDialog.vue'
import ChartCreator from '@/components/charts/chart-creator/ChartCreator.vue'
import { useDisplay } from 'vuetify'

const props = withDefaults(
  defineProps<{
    isEditMode?: boolean
    hideActions?: boolean
  }>(),
  {
    isEditMode: false,
    hideActions: false,
  }
)
const chart = defineModel<SerializedChartType | null>('chart')
const showChartDialog = defineModel<boolean>('showChartDialog')
defineEmits(['save', 'cancel'] as const)

const { t } = useI18n()
const { mobile } = useDisplay()

const chartDialogRef = ref<InstanceType<typeof GenericCustomDialog> | null>(null)
const stepper: Ref<number> = ref(1)

const isFirstStep = computed(() => stepper.value === 1)
const isFinalStep = computed(() => stepper.value === 2)

async function jumpToStep(step: number) {
  stepper.value = step
}

watch(stepper, () => {
  const body = chartDialogRef.value?.bodyRef as HTMLElement | null
  if (!body) return
  requestAnimationFrame(() => body.scrollTo({ top: 0, behavior: 'smooth' }))
})

watchEffect(() => {
  if (!showChartDialog.value) return

  stepper.value = props.isEditMode ? 2 : 1
})
</script>

<template>
  <GenericCustomDialog
    ref="chartDialogRef"
    v-model="showChartDialog"
    max-width="1200px"
    hide-cancel-button
    action-class="justify-space-between"
    :body-class="mobile ? 'pb-0 px-4' : 'pb-0'"
  >
    <template #headerIcon><v-icon :icon="isEditMode ? 'mdi-pencil' : 'mdi-plus'" /></template>
    <template #title>
      {{ isEditMode ? $t('chart_sharing.creator.edit_chart') : $t('chart_sharing.creator.add_chart') }}
    </template>
    <template #body>
      <ChartCreator
        v-model:chart="chart"
        v-model:edit-chart-dialog="showChartDialog"
        v-model:stepper="stepper"
        :hide-actions="props.hideActions"
      />
    </template>
    <template #actions>
      <v-btn
        :prepend-icon="isFirstStep ? 'mdi-close' : 'mdi-chevron-left'"
        variant="tonal"
        @click="isFirstStep ? (showChartDialog = false) : jumpToStep(1)"
      >
        {{ isFirstStep ? t('general_interface.buttons.cancel') : t('general_interface.buttons.back') }}
      </v-btn>
      <v-btn
        :append-icon="isFinalStep ? 'mdi-check' : 'mdi-chevron-right'"
        variant="tonal"
        color="primary"
        @click="isFinalStep ? $emit('save') : jumpToStep(2)"
      >
        {{ isFirstStep ? t('general_interface.buttons.next') : t('general_interface.buttons.save') }}
      </v-btn>
    </template>
  </GenericCustomDialog>
</template>
